/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { useSnackbar } from "notistack";
import { useAsyncData } from "./useAsyncData";
import * as favoritesApi from "../../../api/favorites";
import { downloadFile } from "./useDrive";
import { formatBytes, formatDate } from "./format";

// Module scope keeps the reference stable for useAsyncData.
const fetchFavorites = () => favoritesApi.listFavorites();

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const state = useAsyncData(fetchFavorites, { files: [], folders: [] }, "Could not load favorites.");


  const unstar = async (resourceType, resourceId) => {
    try {
      await favoritesApi.setFavorite({ resourceType, resourceId, favorited: false });
      state.reload();
    } catch (err) {
      enqueueSnackbar(err.message || "Could not update.", { variant: "error" });
    }
  };

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  const rows = [
    ...state.folders.map((f) => ({
      id: f.id, type: "folder", name: f.folder_name, size: null,
      updatedAt: f.updated_at, to: `/drive/folders/${f.id}`,
    })),
    ...state.files.map((f) => ({
      id: f.id, type: "file", name: f.file_name, size: f.size_bytes,
      updatedAt: f.updated_at, to: f.folder_id ? `/drive/folders/${f.folder_id}` : "/drive",
    })),
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600, mb: 2 }}>Favorites</Typography>

      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={state.reload}>Retry</Button>}>
          {state.error}
        </Alert>
      )}

      <Paper variant="outlined">
        {rows.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: "center", fontSize: 13.5, color: "secondaryText.color" }}>
            Nothing starred yet. Use the star on any file or folder to pin it here.
          </Typography>
        ) : (
          rows.map((row, index) => (
            <Box
              key={`${row.type}-${row.id}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 1.25,
                borderTop: index === 0 ? "none" : "1px solid",
                borderColor: "border.color",
                "&:hover": { backgroundColor: "tertiary.main" },
              }}
            >
              <Tooltip title="Remove from favorites">
                <IconButton size="small" onClick={() => unstar(row.type, row.id)}>
                  <StarIcon fontSize="small" sx={{ color: "#F2B01E" }} />
                </IconButton>
              </Tooltip>
              {row.type === "folder" ? (
                <FolderIcon fontSize="small" sx={{ color: "accent.main" }} />
              ) : (
                <InsertDriveFileIcon fontSize="small" sx={{ color: "text.color", opacity: 0.6 }} />
              )}
              <Typography
                sx={{ fontSize: 13.5, flex: 1, cursor: "pointer" }}
                onClick={() => navigate(row.to)}
              >
                {row.name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "secondaryText.color", minWidth: 70 }}>
                {row.type === "folder" ? "—" : formatBytes(row.size)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "secondaryText.color", minWidth: 90, textAlign: "right" }}>
                {formatDate(row.updatedAt)}
              </Typography>
              {row.type === "file" && (
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => downloadFile(row.id, row.name)}>
                    <DownloadOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default FavoritesPage;
